import React, { useRef, useEffect, useState, useMemo } from 'react';
import '../../utils/agGridSetup';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community';
import { ChevronLeftIcon, ChevronRightIcon, MoreVertical, Trash2, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DeviceDashboardDto, ParameterDtoPreferred } from '../../services/deviceApi';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../styles/pages/DeviceTableView.css';
import HelperFunctions, { options, getDeviceStatusLabel } from '../../utils/helperFunctions';
import { getUserTimezone } from '../../utils/timezoneUtils';
import { useAuth } from '../../hooks/useAuth';

// CSS Variables for theming
// const CSS_VARS = {
//   '--color-background': '#ffffff',
//   '--color-primary': '#3b82f6',
//   '--color-primary-light': '#E7F7F4',
//   '--color-hover': '#f9fafb',
//   '--color-border': '#d1d5db',
//   '--color-border-hover': '#9ca3af',
//   '--color-selected': '#dbeafe',
//   '--color-focus': '#eff6ff',
//   '--color-disabled': '#f3f4f6',
//   '--color-text-muted': '#9ca3af',
//   '--color-white': '#ffffff',
//   '--color-scrollbar-track': '#f1f5f9',
//   '--color-scrollbar-thumb': '#cbd5e1',
//   '--color-scrollbar-thumb-hover': '#94a3b8',
// };

interface DeviceTableProps {
  devices: DeviceDashboardDto[];
  parameters: ParameterDtoPreferred[];
  onDeviceClick?: (_deviceId: number) => void;
  searchTerm?: string;
  handleDeleteDevice: (_deviceId: number) => void;
  demoDeviceId: number | null;
  demoUserId: number | null;
}

const DeviceTableView: React.FC<DeviceTableProps> = ({ devices, onDeviceClick, searchTerm = '', parameters, handleDeleteDevice, demoDeviceId, demoUserId }) => {
  // Grid reference for API calls
  const isRestoring = useRef(true);
  const gridRef = useRef<AgGridReact>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();
  const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  const timeZone = userPreferences.timezone || getUserTimezone();
  const theme = document.documentElement.getAttribute("data-theme") ?? "light";
  const { user } = useAuth();
  
  // State for delete modal
  // const [isDeleteDeviceModalOpen, setIsDeleteDeviceModalOpen] = useState(false);
  // const [isDeleting, setIsDeleting] = useState(false);
  // const [isDeletingSuccess, setIsDeletingSuccess] = useState(false);
  // const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  
  // State for dropdown menu
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{top: number, left: number} | null>(null);

  const goToPage = (page: number) => {
    if (gridRef.current?.api) {
      gridRef.current.api.paginationGoToPage(page);
      setCurrentPage(page);
    }
  };

  // Handler for dropdown toggle
  const handleDropdownToggle = (deviceId: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (demoDeviceId == deviceId && demoUserId != user?.userId) return;
    
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.right - 180, // adjust width offset
    });
    
    console.log('handleDropdownToggle called for device:', deviceId, 'current openDropdownId:', openDropdownId);
    setOpenDropdownId(openDropdownId === deviceId ? null : deviceId);
  };

  // Handler for edit device
  const handleEditDevice = (deviceId: number) => {
    setOpenDropdownId(null);
    setDropdownPosition(null);
    navigate(`/edit-device/${deviceId}`);
  };

  // Handler for remove device
  const handleRemoveDevice = (deviceId: number) => {
    handleDeleteDevice(deviceId);
    setOpenDropdownId(null);
    setDropdownPosition(null);
  };

  // Handler for delete confirmation
  /* const handleDeleteDevice = async () => {
    if (!selectedDeviceId) return;
    
    setIsDeleting(true);
    try {
      // Call the context's deleteDevice function which handles API call and refresh
      const response = await deleteDevice(selectedDeviceId.toString());
      
      if (response && response.success) {
        setIsDeletingSuccess(true);
        setTimeout(() => {
          setIsDeleteDeviceModalOpen(false);
          setIsDeletingSuccess(false);
          setSelectedDeviceId(null);
          setIsDeleting(false);
        }, 2000);
      } else {
        // Handle error case
        console.error('Delete failed:', response?.message || 'Unknown error');
        setIsDeleting(false);
        // TODO: Show error message to user
      }
    } catch (error) {
      console.error('Error deleting device:', error);
      setIsDeleting(false);
      // TODO: Show error message to user
    }
   };*/

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (_event: MouseEvent) => {
      if (openDropdownId !== null) {
        setOpenDropdownId(null);
        setDropdownPosition(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdownId]);

  // store the grid column state on resize, sort, move etc
  const storeColumnState = () => {
    if (isRestoring.current) return;

    if (gridRef.current?.api) {
      localStorage.setItem('deviceTableColumnState', JSON.stringify(gridRef.current.api.getColumnState()));
    }
  }

  // Restore the grid api column state on grid ready if state is stored in localStorage other store the state in localStorage
  const restoreColumnState = () => {
    const state = localStorage.getItem("deviceTableColumnState");
    if (state) {
      isRestoring.current = true;
      gridRef.current?.api?.applyColumnState({
        state: JSON.parse(state),
        applyOrder: true,
      });
      setTimeout(() => {
        isRestoring.current = false;
      }, 100);
    }else {
      isRestoring.current = false;
    }
  }

  // Helper comparators for sorting
  const nullSafeNumberComparator = (a: number | null, b: number | null) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;   // nulls → bottom
    if (b == null) return -1;  // nulls → bottom
    return a - b;
  };

  const nullSafeDateComparator = (a: number | null, b: number | null) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a - b;
  };

  // Column definitions
    // generateColumnDefs with nulls always at bottom
const generateColumnDefs = (): ColDef[] => {
  return [
    {
      headerName: 'Device Name',
      field: 'deviceName',
      cellRenderer: (params: any) => {
        const device = params.data;
        return (
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 ${device.deviceImageUrl ? 'rounded-full' : 'rounded-lg'} flex items-center justify-center overflow-hidden`}>
              <img 
                src={device.deviceImageUrl || `${process.env.PUBLIC_URL}/DeviceSetup/device.svg`} 
                alt="Device" 
                className="w-full h-full object-cover"
              />
            </div>
            <span 
              className="text-[#005EE5] dark:text-[#A9CCFE] hover:underline cursor-pointer"
              onClick={() => onDeviceClick?.(device.deviceId)}
            >
              {device.deviceName || 'Unnamed Device'}
            </span>
          </div>
        );
      },
      minWidth: 220,
      flex: 1,
      sortable: true,
      filter: false,
      pinned: 'left',
      wrapText: true,
      resizable: false,
    },
    {
      headerName: 'Model Number',
      field: 'deviceModel',
      minWidth: 160,
      flex: 1,
      sortable: true,
      filter: false,
      resizable: false,
    },
    // {
    //   headerName: 'Environment Mode',
    //   field: 'environmentMode',
    //   cellRenderer: (params: any) => {
    //     const environmentMode = params.value;
    //     return (
    //     <>
    //       {environmentMode != null ? (
    //         <div className='flex items-center gap-2'> 
    //           <img src={`${process.env.PUBLIC_URL}/EnvironmentModeIcons/${environmentMode}.svg`} alt="Environment Mode" className='w-6 h-6 object-cover' />
    //           <span className='text-[var(--color-sub-title-text)] font-normal text-sm'>{environmentMode}</span>
    //         </div>
    //       ) : (
    //         'N/A'
    //       )}
    //     </>
    //     );
    //   },
    //   minWidth: 200,
    //   flex: 1,
    //   sortable: true,
    //   filter: false,
    //   resizable: false,
    // },
    {
      headerName: 'AQI',
      field: 'aqiValue',
      sortable: true,
      valueGetter: (params: any) => {
        const aqi: any = Object.values(params.data.parameters).find((value: any) => value.parameterName === 'AQI');
        return aqi?.value != null ? Number(aqi.value) : null;
      },
      comparator: nullSafeNumberComparator,
      cellRenderer: (params: any) => {
        const aqiValue = params.value != null ? params.value.toFixed(0) : 'N/A';
        return (
          <span className='px-3 py-1.5 rounded-md text-sm text-[var(--color-sub-title-text)] font-normal' style={{ background: `${theme == 'light' ? HelperFunctions.GetParameterColor(aqiValue, 'AQI') : HelperFunctions.GetParameterColor(aqiValue, 'AQI') + 80}` }}>
            {aqiValue}
          </span>
        );
      },
      minWidth: 150,
      flex: 1,
      resizable: false,
      filter: false,
    },

    ...parameters
      .filter((parameter) => parameter.parameterName !== 'AQI')
      .map((parameter) => ({
        headerName: `${parameter.parameterName}${parameter.unit ? ` (${parameter.unit})` : ''}`,
        field: String(parameter.driverID),
        sortable: true,
        valueGetter: (params: any) => {
          const param = params.data.parameters[parameter.driverID];
          return param?.value != null ? Number(param.value) : null;
        },
        comparator: nullSafeNumberComparator,
        cellRenderer: (params: any) => {
          // if(parameter.parameterName === 'Temperature' || parameter.parameterName === 'Humidity') {
          //   return <span>{params.value != null ? Math.round(params.value) : "N/A"}</span>;
          // }
          const value =
            params.value != null
              ? Number.isInteger(params.value)
                ? params.value
                : params.value < 0.01 ? params.value.toFixed(4) : params.value.toFixed(2)
              : 'N/A';
          return <span>{value}</span>;  
        },
        minWidth: (parameter.parameterName.length + parameter.unit?.length) > 10 ? 185 : 155,
        flex: parameter.parameterName.length + (parameter.unit?.length ?? 0) >= 8 ? 1.75 : 1.25,
        filter: false,
      })),
      {
        headerName: "Last Updated",
        field: "lastUpdated",
        sortable: true,
        valueGetter: (params) => {
          const rawValue = params.data.lastUpdated;
          if (!rawValue) return null;
          const d = new Date(rawValue);
          return isNaN(d.getTime()) ? null : d.getTime();
        },
        comparator: nullSafeDateComparator,
        cellRenderer: (params: any) => {
          const rawValue = params.data.lastUpdated;
      
          if (!rawValue) {
            return <span>N/A</span>; // show placeholder if null/empty
          }
      
          const date = new Date(rawValue);
          return <span>{date.toLocaleString('en-US', {timeZone: timeZone, ...options})}</span>;
        },
        minWidth: 200,
        flex: 1,
        filter: false,
      },
    {
      headerName: 'Status',
      field: 'isActive',
      cellRenderer: (params: any) => {
        const { label, variant } = getDeviceStatusLabel(params.value, params.data?.operationalStatus);
        const dotColor = variant === 'active' ? 'bg-[#47C08F] animate-pulse' : variant === 'initializing' ? 'bg-[rgb(255,179,110)] animate-pulse' : 'bg-[#A2A5C1]';
        const textColor = variant === 'active' ? 'text-[#47C08F]' : variant === 'initializing' ? 'text-[rgb(255,179,110)]' : 'text-[#A2A5C1]';
        return (
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${dotColor}`}></div>
            <span className={textColor}>{label}</span>
          </div>
        );
      },
      minWidth: 100,
      width: 125,
      sortable: true,
      filter: false,
      resizable: false,
      pinned: 'right',
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (params: any) => {
        const deviceId = params.data.deviceId;
        return (
          <div className="relative flex justify-center">
            <button
              onClick={(e) => {
                console.log('Three dots clicked for device:', deviceId);
                handleDropdownToggle(deviceId, e);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className="p-2 hover:bg-[var(--button-hover)] rounded-full transition-colors"
            >
              <MoreVertical className="w-4 h-4 text-[var(--color-text)]" />
            </button>
          </div>
        );

      },
      minWidth: 100,
      width: 100,
      sortable: false,
      filter: false,
      resizable: false,
      pinned: 'right',
    },
  ];
}

  const columnDefs: ColDef[] = useMemo(() => generateColumnDefs(), [parameters, devices]);

  // Apply search filter when searchTerm prop changes
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('quickFilterText', searchTerm);
    }
  }, [searchTerm]);

  // Grid options
  const gridOptions: GridOptions = useMemo(() => ({    
    defaultColDef: {
      resizable: true,
      sortable: true,
      filter: false,
    },
    rowData: devices,
    columnDefs: columnDefs,
    pagination: true,
    paginationPageSize: 10,
    suppressPaginationPanel: true,
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    suppressRowDeselection: true,
    suppressDragLeaveHidesColumns: true,
    suppressColumnMoveAnimation: true,
    domLayout: 'normal',
    rowHeight: 60,
    headerHeight: 75,
    overlayNoRowsTemplate: '<span class="custom-no-rows">No data found</span>',

    icons: {
    sortAscending: '<span style="font-size:12px;">▲</span>',
    sortDescending: '<span style="font-size:12px;">▼</span>',
  },
  onColumnResized: storeColumnState,
  onColumnPinned: storeColumnState,
  onSortChanged: storeColumnState,
  onColumnMoved: storeColumnState,
  onGridReady: () => {
    setTimeout(() => {
      restoreColumnState();
    }, 100);
  },
  }), [columnDefs, devices]);

  

  return (
    <div className="ag-theme-alpine w-full device-view-container">
      <AgGridReact
         ref={gridRef}
         {...gridOptions}
         className="device-table-grid zoomOutLevel"
         popupParent={document.body}
         onPaginationChanged={() => {
           if (gridRef.current?.api) {
             setCurrentPage(gridRef.current.api.paginationGetCurrentPage());
             setTotalPages(gridRef.current.api.paginationGetTotalPages());
           }
         }}
       />

      {totalPages > 1 && <div className='pagination-container h-auto zoomOutLevel'>
        <button disabled={currentPage <= 0} className='pagination-button pagination-previous' onClick={() => goToPage(currentPage - 1)}>
          <ChevronLeftIcon className='w-4 h-4' />
        </button>

        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            onClick={() => goToPage(i)}
            className={`pagination-button ${currentPage === i ? 'active' : '' }`}
          >
            {i + 1}
          </button>
        ))}

        <button disabled={currentPage == totalPages - 1} className='pagination-button pagination-next' onClick={() => goToPage(currentPage + 1)}>
          <ChevronRightIcon className='w-4 h-4' />
        </button>
      </div>}

      {/* <DeleteDeviceModal
        isOpen={isDeleteDeviceModalOpen}
        onClose={() => setIsDeleteDeviceModalOpen(false)}
        onConfirm={() => handleDeleteDevice()}
        isDeleting={isDeleting}
        isDeletingSuccess={isDeletingSuccess}
      /> */}

      {/* External Dropdown */}
      {openDropdownId !== null && dropdownPosition && (
        <div 
          className="fixed w-40 bg-[var(--modal-background)] rounded-[0.625rem] border border-[var(--button-border-color)] z-[9999]"
          style={{
            top: dropdownPosition?.top,
            left: dropdownPosition?.left,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEditDevice(openDropdownId);
              }}
              className="w-full p-2 rounded-sm font-normal text-left text-xs text-[var(--color-sub-title-text)] hover:bg-[var(--button-hover)] flex items-center gap-3"
            >
              <Pencil  className="w-4 h-4 text-[var(--color-sub-title-text)]" />
              Edit Details
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRemoveDevice(openDropdownId);
              }}
              className="w-full p-2 rounded-sm text-left font-normal text-xs text-[#E67E7D] hover:bg-[var(--button-hover)] flex items-center gap-3"
            >
              <Trash2 className="w-4 h-4 text-[#E67E7D]" />
              Remove Device
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceTableView;
